<script>
import * as d3 from 'd3'

import CandelaComponent from '@/components/CandelaComponent'

import {
  LineEffect,
  areas,
  effects,
  lines,
  points,
  point_visibility
} from '@/components/plots/atoms'

export default {
  computed: {
    areas_1() { return areas(this.y, this.axis_y, this.points_1, this.curve_type) },
    areas_2() { return areas(this.y2, this.axis_y2, this.points_2, this.curve_type) },
    colors_1() {
      return this.y.data.map((x, i) => {
        return this.settings.plot_colors.value(i)
      })
    },
    colors_2() {
      const offset = this.y.data ? this.y.data.length : 0
      return this.y2.data.map((x, i) => {
        return this.settings.plot_colors.value(i + offset)
      })
    },
    curve_type() { return d3.curveLinear },
    effects_1() { return effects(this.y, LineEffect) },
    effects_2() { return effects(this.y2, LineEffect) },
    lines_1() { return lines(this.points_1, this.curve_type) },
    lines_2() { return lines(this.points_2, this.curve_type) },
    points_1() { return points(this.x, this.y, this.axis_x, this.axis_y) },
    points_2() { return points(this.x2, this.y2, this.axis_x2, this.axis_y2) },
    point_visibility_1() { return point_visibility(this.y) },
    point_visibility_2() { return point_visibility(this.y2) }
  },
  data() {
    return {
      axis_x: null,
      axis_y: null,
      axis_x2: null,
      axis_y2: null
    }
  },
  extends: CandelaComponent,
  methods: {
    update(axes) {
      this.axis_x = axes.x
      this.axis_y = axes.y
      this.axis_x2 = axes.x2
      this.axis_y2 = axes.y2
    }
  },
  name: 'candela-area-chart',
  props: {
    x: {type: Object, default: () => null},
    y: {type: Object, default: () => null},
    x2: {type: Object, default: () => null},
    y2: {type: Object, default: () => null}
  }
}
</script>

<template>
<div class='candela-component'>
  <candela-plot :id='id' :frame='frame' @update='update'>
    <candela-axis v-if='x' slot='axis_x' v-bind='x'/>
    <candela-axis v-if='y' slot='axis_y' v-bind='y'/>
    <candela-axis v-if='x2' slot='axis_x2' v-bind='x2'/>
    <candela-axis v-if='y2' slot='axis_y2' v-bind='y2'/>

    <g slot='background'>
      <candela-grid-lines v-if='settings.show_grid.value'
        :axis_x='axis_x' :axis_y='axis_y'/>
    </g>

    <g slot='content'>
      <path v-for='(a, i) in areas_1' :key='`xy1-area-${i}`'
        :d='a'
        :fill='colors_1[i]'
        opacity='0.5' />

      <path v-for='(a, i) in areas_2' :key='`xy2-area-${i}`'
        :d='a'
        :fill='colors_2[i]'
        opacity='0.5' />

      <path v-for='(l, i) in lines_1' :key='`xy1-line-${i}`'
        fill='none' :d='l'
        :stroke='colors_1[i]'
        :stroke-width='effects_1[i].w'
        :stroke-dasharray='effects_1[i].d'
        :stroke-linecap='effects_1[i].c'/>

      <g v-for='(points, i) in points_1' :key='`xy1-pionts-${i}`'>
        <g v-if='point_visibility_1[i]'>
          <circle v-for='(p, j) in points' :key='`xy1-point-${j}`'
            :cx='p[0]' :cy='p[1]' r='4' :fill='colors_1[i]'/>
        </g>
      </g>

      <path v-for='(l, i) in lines_2' :key='`xy2-line-${i}`'
        fill='none' :d='l'
        :stroke='colors_2[i]'
        :stroke-width='effects_2[i].w'
        :stroke-dasharray='effects_2[i].d'
        :stroke-linecap='effects_2[i].c'/>

      <g v-for='(points, i) in points_2' :key='`xy2-points-${i}`'>
        <g v-if='point_visibility_2[i]'>
          <circle v-for='(p, j) in points' :key='`xy2-point-${j}`'
            :cx='p[0]' :cy='p[1]' r='4' :fill='colors_2[i]'/>
        </g>
      </g>
    </g>

    <div slot='bottom-content'>
      <candela-legend v-if='y && y.index && settings.show_legend.value'
        :colors='colors_1' :index='y.index' :title='y.variable'/>

      <candela-legend v-if='y2 && y2.index && settings.show_legend.value'
        :colors='colors_2' :index='y2.index' :title='y2.variable'/>
    </div>
  </candela-plot>
</div>
</template>
